import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Edit2,
  ExternalLink,
  FileText,
  ImagePlus,
  Images,
  Layers,
  Loader2,
  DollarSign,
  Package,
  Pencil,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Shield,
  ShoppingBag,
  Store,
  Tag,
  TrendingUp,
  Trash2,
  X,
} from "lucide-react";
import { useGetEbayDemand } from "@tcg-web/api-client-react";
import { BackButton } from "@/components/BackButton";
import { Badge, productStatusBadge } from "@/components/Badge";
import { FilterSelect } from "@/components/FilterSelect";
import { PhotoOrderer, type PhotoItem } from "@/components/PhotoOrderer";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  generateId,
  supplyCostFor,
  useApp,
  type Card,
  type ListingTemplate,
  type LiveListing,
  type PriceHistoryEntry,
  type PriceSource,
  type Product,
  type ProductStatus,
  type Sale,
  type SaleVenue,
} from "@/context/AppContext";
import { conditionAdjust, fetchCardMarketPrice } from "@/features/tcgApi";
import { useCardsByIds } from "@/features/useCardsByIds";
import {
  fetchEbaySales,
  mergeEbaySalesIntoHistory,
  type EbaySale,
} from "@/features/salesHistoryFetcher";
import {
  generateBundles,
  pickDefaultListingType,
  type BundleSuggestion,
} from "@/features/bundleEngine";
import { enrichBundleSuggestions, rankBundles } from "@/features/bundleRanking";
import {
  buildDescriptionForProduct,
  hasTemplatePlaceholders,
  resolveTemplatePlaceholders,
  type GeneratedDescription,
} from "@/features/bundleDescription";
import {
  classifyDemand,
  describeDemandSuggestion,
  UNKNOWN_DEMAND_RATIO,
} from "@/features/demandPricing";
import { apiFetch } from "@/lib/apiClient";
import { RequireRole } from "@/lib/auth";
import { deleteImage, imageUrl, uploadImage } from "@/features/imageUpload";
import { compositeOverlays, stitchBundlePanel } from "@/features/imageStitcher";

interface ProductSearch {
  openGoLive?: "true";
  relistFromListingId?: string;
  relistAskingPrice?: string;
  relistListingType?: "Auction" | "FixedPrice";
  sellSimilarFromItemId?: string;
  sellSimilarTitle?: string;
  sellSimilarPrice?: string;
  sellSimilarListingType?: "Auction" | "FixedPrice";
  sellSimilarReservePrice?: string;
  sellSimilarBuyItNowPrice?: string;
  initialSupplyIds?: string;
}

function asStr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}
function asListingType(v: unknown): "Auction" | "FixedPrice" | undefined {
  return v === "Auction" || v === "FixedPrice" ? v : undefined;
}

export const Route = createFileRoute("/products/$id")({
  component: ProductDetailPage,
  validateSearch: (raw): ProductSearch => ({
    openGoLive: raw.openGoLive === "true" ? "true" : undefined,
    relistFromListingId: asStr(raw.relistFromListingId),
    relistAskingPrice: asStr(raw.relistAskingPrice),
    relistListingType: asListingType(raw.relistListingType),
    sellSimilarFromItemId: asStr(raw.sellSimilarFromItemId),
    sellSimilarTitle: asStr(raw.sellSimilarTitle),
    sellSimilarPrice: asStr(raw.sellSimilarPrice),
    sellSimilarListingType: asListingType(raw.sellSimilarListingType),
    sellSimilarReservePrice: asStr(raw.sellSimilarReservePrice),
    sellSimilarBuyItNowPrice: asStr(raw.sellSimilarBuyItNowPrice),
    initialSupplyIds: asStr(raw.initialSupplyIds),
  }),
});

// TNK-214 — pick/pack are POST-sale fulfillment (right before ship), not a
// pre-listing step. A product's cards are held In Package from when it's made,
// stay held until it sells, then it's picked → packed → shipped.
const STATUS_CYCLE: ProductStatus[] = [
  "Draft",
  "Ready to List",
  "Listed",
  "Sold",
  "Picked",
  "Packed",
  "Shipped",
];

const PRICE_SOURCES: PriceSource[] = [
  "Manual",
  "TCGplayer",
  "eBay Sold",
  "Other",
];

const VENUES: SaleVenue[] = [
  "eBay",
  "Facebook Marketplace",
  "Local",
  "TCGplayer",
  "Website",
  "Other",
];

type GoLiveDuration = "GTC" | 0.5 | 1 | 3 | 5 | 7 | 10 | 30;
type GoLiveCondition = "NM" | "LP" | "MP" | "HP" | "Damaged";
type GoLiveListingType = "FixedPrice" | "Auction";

function ProductDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const {
    products,
    cards,
    liveListings,
    sales,
    listingTemplates,
    shippingSupplies,
    deleteProductAndUnpackCards,
    archiveProduct,
    restoreProduct,
    updateProduct,
    addSale,
    updateSale,
    updateCard,
    bulkUpdateCards,
    addCardsToProduct,
    removeCardFromProduct,
    goLive,
    updateListingSupplies,
    storeImages,
    bundleSettings,
  } = useApp();

  const product = useMemo(
    () => products.find((p) => p.id === id),
    [products, id],
  );

  const packagedCardIds = useMemo(() => product?.cardIds ?? [], [product]);

  // TNK-155 phase C: server-fetch this product's packaged cards by id
  // list instead of iterating the entire inventory. `unlistedCards`
  // below stays on the in-memory scan for now — that's the bundle
  // engine's input and folds into phase E.
  const { cards: packagedCards } = useCardsByIds(packagedCardIds);

  const unlistedCards = useMemo(
    () => cards.filter((c) => c.listingStatus === "Unlisted"),
    [cards],
  );

  // TNK-102: under cross-listing, listings persist after sale (status flips
  // instead of deletion). For the product-detail summary we only care about
  // the currently OPEN listing — anything closed shouldn't block the Go
  // Live button. The /live route shows the full history with status filters.
  const existingLiveListing = useMemo(
    () =>
      liveListings.find(
        (l) => l.productId === id && (l.status ?? "Active") === "Active",
      ),
    [liveListings, id],
  );

  const rawBundleSuggestions = useMemo(
    () => generateBundles(unlistedCards, bundleSettings),
    [unlistedCards, bundleSettings],
  );

  const enrichedBundleSuggestions = useMemo(
    () =>
      enrichBundleSuggestions(rawBundleSuggestions, {
        products,
        liveListings,
        sales,
      }),
    [rawBundleSuggestions, products, liveListings, sales],
  );

  const bundleSuggestions = useMemo(
    () => rankBundles(enrichedBundleSuggestions, bundleSettings.sortMode),
    [enrichedBundleSuggestions, bundleSettings.sortMode],
  );

  const [showPicker, setShowPicker] = useState(false);
  const [bundleSuggestionsShowAll, setBundleSuggestionsShowAll] =
    useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerFilterSet, setPickerFilterSet] = useState("All");
  const [pickerFilterRarity, setPickerFilterRarity] = useState("All");
  const [pickerFilterCondition, setPickerFilterCondition] = useState("All");
  const [pkgFilterSet, setPkgFilterSet] = useState("All");
  const [pkgFilterRarity, setPkgFilterRarity] = useState("All");
  const [pkgFilterCondition, setPkgFilterCondition] = useState("All");
  const [pkgListExpanded, setPkgListExpanded] = useState(false);
  const [bundleSuggestionsExpanded, setBundleSuggestionsExpanded] =
    useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bundlePickerCardIds, setBundlePickerCardIds] =
    useState<Set<string> | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [refreshingPriceIds, setRefreshingPriceIds] = useState<Set<string>>(
    new Set(),
  );
  const [noPriceFoundIds, setNoPriceFoundIds] = useState<Set<string>>(
    new Set(),
  );
  const [priceRefreshErrorIds, setPriceRefreshErrorIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [bulkRefreshBanner, setBulkRefreshBanner] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const bulkRefreshBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(
    new Set(),
  );
  const [logPriceCardId, setLogPriceCardId] = useState<string | null>(null);
  const [logPriceValue, setLogPriceValue] = useState("");
  const [logPriceSource, setLogPriceSource] = useState<PriceSource>("Manual");
  const [logPriceDate, setLogPriceDate] = useState("");
  const [logPriceError, setLogPriceError] = useState("");

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (bulkRefreshBannerTimer.current) {
        clearTimeout(bulkRefreshBannerTimer.current);
      }
    };
  }, []);

  // ── Go-Live form state ───────────────────────────────────────────────────
  const [showGoLiveForm, setShowGoLiveForm] = useState(false);
  const [goLiveVenue, setGoLiveVenue] = useState<SaleVenue>("eBay");
  const [goLivePrice, setGoLivePrice] = useState("");
  const [goLiveTitle, setGoLiveTitle] = useState("");
  const [goLiveEndsAt, setGoLiveEndsAt] = useState("");
  const [pushToEbay, setPushToEbay] = useState(false);
  const [goLiveDuration, setGoLiveDuration] = useState<GoLiveDuration>(7);
  const [ebayPushLoading, setEbayPushLoading] = useState(false);
  const [ebayStatus, setEbayStatus] = useState<
    "loading" | "connected" | "disconnected"
  >("loading");
  const [ebayPushError, setEbayPushError] = useState("");
  const [goLiveUploadProgress, setGoLiveUploadProgress] = useState<
    string | null
  >(null);
  const [goLiveListingType, setGoLiveListingType] =
    useState<GoLiveListingType>("Auction");
  const [goLiveListingTypeReason, setGoLiveListingTypeReason] = useState<
    string | null
  >(null);
  const [goLiveStartingBid, setGoLiveStartingBid] = useState("");
  const [goLiveReservePrice, setGoLiveReservePrice] = useState("");
  const [goLiveCondition, setGoLiveCondition] = useState<GoLiveCondition>("NM");
  const [goLiveShipping, setGoLiveShipping] = useState("1.10");
  const [goLiveDescription, setGoLiveDescription] = useState("");
  const [generatedDescription, setGeneratedDescription] =
    useState<GeneratedDescription | null>(null);
  const [goLiveFieldErrors, setGoLiveFieldErrors] = useState<
    Record<string, string>
  >({});
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedSupplyIds, setSelectedSupplyIds] = useState<Set<string>>(
    new Set(),
  );
  const [panelImageRef, setPanelImageRef] = useState<string | null>(null);
  const [panelGenerating, setPanelGenerating] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  // Watermark + logo overlays (TNK-25 phase 2b) — overlay choices apply to the
  // bundle panel, the result lives in compositeImageRef and replaces the raw
  // panel as pictureUrls[0] when present.
  const [applyWatermark, setApplyWatermark] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.35);
  const [applyLogo, setApplyLogo] = useState(false);
  const [logoOpacity, setLogoOpacity] = useState(0.9);
  const [compositeImageRef, setCompositeImageRef] = useState<string | null>(
    null,
  );
  const [compositeGenerating, setCompositeGenerating] = useState(false);
  const [compositeError, setCompositeError] = useState<string | null>(null);
  const userEditedStartingBidRef = useRef(false);

  // ── Listing photo picker (TNK-28) ─────────────────────────────────────────
  // `photoOrder` holds item ids in display order; `includedPhotos` is the
  // upload set. The bundle panel is represented by a sentinel id so its slot
  // survives panel regeneration / watermark recomposite (whose ref changes).
  const [photoOrder, setPhotoOrder] = useState<string[]>([]);
  const [includedPhotos, setIncludedPhotos] = useState<Set<string>>(new Set());
  const seenPhotosRef = useRef<Set<string>>(new Set());

  // ── Edit supplies on existing live listing ───────────────────────────────
  const [showEditSupplies, setShowEditSupplies] = useState(false);
  const [editSupplyIds, setEditSupplyIds] = useState<Set<string>>(new Set());

  // Build a stable search query for the demand lookup using the first packaged
  // card; matches mobile parity so React Query dedupes across sibling variants.
  const firstPackagedCard = packagedCards[0];
  const demandQuery = useMemo(() => {
    if (!firstPackagedCard) return "";
    return [firstPackagedCard.name, firstPackagedCard.setName]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [firstPackagedCard]);

  const demandEnabled =
    showGoLiveForm &&
    goLiveListingType === "Auction" &&
    demandQuery.length > 0 &&
    product != null &&
    product.targetSalePrice > 0;

  const demandParams = useMemo(
    () => ({ q: demandQuery, categoryId: "183050" }),
    [demandQuery],
  );

  const demandQueryResult = useGetEbayDemand(demandParams, {
    query: {
      queryKey: ["/api/ebay/demand", demandParams] as const,
      enabled: demandEnabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: false,
    },
  });

  const demandSuggestion = useMemo(() => {
    if (demandQueryResult.data) return classifyDemand(demandQueryResult.data);
    return { tier: "unknown" as const, startBidRatio: UNKNOWN_DEMAND_RATIO };
  }, [demandQueryResult.data]);

  // Keep auction starting bid in sync with the demand-aware suggestion unless
  // the seller has typed over it. Fires when the form opens, when the demand
  // lookup resolves, and when the seller flips listing type to Auction.
  useEffect(() => {
    if (!showGoLiveForm) return;
    if (goLiveListingType !== "Auction") return;
    if (userEditedStartingBidRef.current) return;
    if (!product || product.targetSalePrice <= 0) return;
    const next = (
      product.targetSalePrice * demandSuggestion.startBidRatio
    ).toFixed(2);
    setGoLiveStartingBid((prev) => (prev === next ? prev : next));
  }, [
    showGoLiveForm,
    goLiveListingType,
    demandSuggestion.startBidRatio,
    product,
  ]);

  const checkEbayStatus = useCallback(async (): Promise<
    "connected" | "disconnected"
  > => {
    setEbayStatus("loading");
    try {
      const res = await apiFetch("/api/ebay/auth/status");
      if (!res.ok) {
        setEbayStatus("disconnected");
        return "disconnected";
      }
      const data = (await res.json()) as { connected: boolean };
      const s = data.connected ? "connected" : "disconnected";
      setEbayStatus(s);
      return s;
    } catch {
      setEbayStatus("disconnected");
      return "disconnected";
    }
  }, []);

  const handleDurationChange = useCallback((d: GoLiveDuration) => {
    setGoLiveDuration(d);
    if (d === "GTC") {
      setGoLiveEndsAt("");
    } else if (d === 0.5) {
      const end = new Date(Date.now() + 12 * 60 * 60 * 1000);
      setGoLiveEndsAt(end.toISOString());
    } else {
      const end = new Date(Date.now() + d * 24 * 60 * 60 * 1000);
      setGoLiveEndsAt(end.toISOString());
    }
  }, []);

  const openGoLiveForm = useCallback(async () => {
    if (!product) return;
    const firstCard = packagedCards[0];
    const defaultCondition: GoLiveCondition =
      firstCard?.condition &&
      ["NM", "LP", "MP", "HP", "Damaged"].includes(firstCard.condition)
        ? (firstCard.condition as GoLiveCondition)
        : "NM";
    const listingTitle =
      product.listingTitle || product.name || product.productType;
    const generated = buildDescriptionForProduct(product, packagedCards);
    setGeneratedDescription(generated);
    const savedDescription = product.description?.trim() ?? "";
    let defaultDescription: string;
    if (savedDescription && hasTemplatePlaceholders(savedDescription)) {
      const priceStr =
        product.targetSalePrice && product.targetSalePrice > 0
          ? `$${product.targetSalePrice.toFixed(2)}`
          : "";
      defaultDescription = resolveTemplatePlaceholders(savedDescription, {
        firstCard,
        condition: defaultCondition,
        price: priceStr,
        generated: generated.plainText,
      });
    } else if (savedDescription) {
      defaultDescription = savedDescription;
    } else {
      defaultDescription = generated.plainText;
    }
    setGoLiveTitle(listingTitle);
    setGoLiveEndsAt("");
    setGoLiveVenue("eBay");
    setPushToEbay(false);
    setEbayPushError("");
    setGoLiveUploadProgress(null);
    handleDurationChange(7);
    setGoLiveReservePrice("");
    userEditedStartingBidRef.current = false;
    const smart = pickDefaultListingType(packagedCards, product.productType);
    setGoLiveListingType(smart.listingType);
    setGoLiveListingTypeReason(smart.reason);
    setGoLiveStartingBid(
      product.targetSalePrice > 0
        ? (product.targetSalePrice * demandSuggestion.startBidRatio).toFixed(2)
        : "",
    );
    setGoLivePrice(
      product.targetSalePrice > 0 ? product.targetSalePrice.toFixed(2) : "",
    );
    if (smart.listingType === "FixedPrice") {
      handleDurationChange("GTC");
    }
    setGoLiveCondition(defaultCondition);
    setGoLiveShipping("1.10");
    setGoLiveDescription(defaultDescription);
    setGoLiveFieldErrors({});
    setShowTemplatePicker(false);
    setSelectedSupplyIds(new Set());
    setPanelImageRef(null);
    setPanelError(null);
    setPanelGenerating(false);
    // Default-on watermark/logo when the user has uploaded them under /settings.
    // Auto-applied when the panel is generated; user can toggle off per-listing.
    setApplyWatermark(!!storeImages.watermark);
    setWatermarkOpacity(0.35);
    setApplyLogo(!!storeImages.logo);
    setLogoOpacity(0.9);
    setCompositeImageRef(null);
    setCompositeError(null);
    setCompositeGenerating(false);
    setShowGoLiveForm(true);
    const status = await checkEbayStatus();
    if (status === "connected") setPushToEbay(true);
  }, [
    product,
    packagedCards,
    demandSuggestion.startBidRatio,
    handleDurationChange,
    checkEbayStatus,
    storeImages.watermark,
    storeImages.logo,
  ]);

  const generateBundlePanel = useCallback(async () => {
    setPanelError(null);
    const refs = packagedCards
      .map((c) => c.imageFrontRef)
      .filter((r): r is string => !!r);
    if (refs.length === 0) {
      setPanelError(
        "No card photos yet — upload front photos on each card first.",
      );
      return;
    }
    setPanelGenerating(true);
    try {
      const urls = refs.map((r) => imageUrl(r)).filter((u): u is string => !!u);
      const blob = await stitchBundlePanel(urls);
      const file = new File([blob], `bundle-${id}.jpg`, { type: "image/jpeg" });
      const newRef = await uploadImage(file);
      if (panelImageRef) void deleteImage(panelImageRef);
      setPanelImageRef(newRef);
    } catch (e) {
      setPanelError(
        e instanceof Error ? e.message : "Could not generate panel.",
      );
    } finally {
      setPanelGenerating(false);
    }
  }, [packagedCards, id, panelImageRef]);

  const removeBundlePanel = useCallback(() => {
    if (panelImageRef) void deleteImage(panelImageRef);
    if (compositeImageRef) void deleteImage(compositeImageRef);
    setPanelImageRef(null);
    setCompositeImageRef(null);
    setPanelError(null);
    setCompositeError(null);
  }, [panelImageRef, compositeImageRef]);

  // Regenerate the watermark/logo composite whenever the underlying panel or
  // any overlay setting changes. Debounced 300ms so slider drags don't fire a
  // request per pixel. Old composite is cleaned up so we don't leak refs.
  useEffect(() => {
    const clearComposite = () => {
      setCompositeImageRef((prev) => {
        if (prev) void deleteImage(prev);
        return null;
      });
      setCompositeError(null);
    };
    if (!panelImageRef) {
      clearComposite();
      return;
    }
    const wmRef = applyWatermark ? storeImages.watermark : undefined;
    const logoRef = applyLogo ? storeImages.logo : undefined;
    if (!wmRef && !logoRef) {
      clearComposite();
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setCompositeGenerating(true);
      setCompositeError(null);
      try {
        const baseRes = await fetch(
          `/api/images/${encodeURIComponent(panelImageRef)}`,
        );
        if (!baseRes.ok) throw new Error(`HTTP ${baseRes.status}`);
        const baseBlob = await baseRes.blob();
        const compBlob = await compositeOverlays(baseBlob, {
          watermarkUrl: wmRef ? (imageUrl(wmRef) ?? undefined) : undefined,
          watermarkOpacity,
          logoUrl: logoRef ? (imageUrl(logoRef) ?? undefined) : undefined,
          logoOpacity,
        });
        if (cancelled) return;
        const file = new File([compBlob], `bundle-comp-${id}.jpg`, {
          type: "image/jpeg",
        });
        const newRef = await uploadImage(file);
        if (cancelled) {
          void deleteImage(newRef);
          return;
        }
        setCompositeImageRef((prev) => {
          if (prev && prev !== newRef) void deleteImage(prev);
          return newRef;
        });
      } catch (e) {
        if (!cancelled) {
          setCompositeError(
            e instanceof Error ? e.message : "Could not composite overlays.",
          );
        }
      } finally {
        if (!cancelled) setCompositeGenerating(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    panelImageRef,
    applyWatermark,
    watermarkOpacity,
    applyLogo,
    logoOpacity,
    storeImages.watermark,
    storeImages.logo,
    id,
    // compositeImageRef intentionally omitted — including it would re-fire the
    // effect on every successful generate.
  ]);

  // ── Listing photo catalog (TNK-28) ────────────────────────────────────────
  const PANEL_PHOTO_ID = "__bundle_panel__";
  const headPanelRef = compositeImageRef ?? panelImageRef;
  const photoCatalog = useMemo<PhotoItem[]>(() => {
    const items: PhotoItem[] = [];
    if (headPanelRef) {
      items.push({
        id: PANEL_PHOTO_ID,
        ref: headPanelRef,
        kind: "panel",
        label: "Bundle panel",
      });
    }
    for (const c of packagedCards) {
      if (c.imageFrontRef) {
        items.push({
          id: c.imageFrontRef,
          ref: c.imageFrontRef,
          kind: "front",
          label: c.name,
        });
      }
    }
    for (const c of packagedCards) {
      if (c.imageBackRef) {
        items.push({
          id: c.imageBackRef,
          ref: c.imageBackRef,
          kind: "back",
          label: `${c.name} (back)`,
        });
      }
    }
    return items;
  }, [headPanelRef, packagedCards]);

  // Reconcile order + inclusion when the candidate set changes. Preserves the
  // user's manual ordering / inclusion for surviving items; new fronts and the
  // panel default to included, card backs default to opt-in (excluded).
  useEffect(() => {
    const ids = photoCatalog.map((i) => i.id);
    const idSet = new Set(ids);
    setPhotoOrder((prev) => {
      const kept = prev.filter((pid) => idSet.has(pid));
      const added = ids.filter((pid) => !kept.includes(pid));
      return [...kept, ...added];
    });
    if (!headPanelRef) seenPhotosRef.current.delete(PANEL_PHOTO_ID);
    setIncludedPhotos((prev) => {
      const next = new Set([...prev].filter((pid) => idSet.has(pid)));
      for (const item of photoCatalog) {
        if (!seenPhotosRef.current.has(item.id) && item.kind !== "back") {
          next.add(item.id);
        }
      }
      return next;
    });
    for (const item of photoCatalog) seenPhotosRef.current.add(item.id);
  }, [photoCatalog, headPanelRef]);

  const togglePhoto = useCallback((photoId: string) => {
    setIncludedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }, []);

  // Final ordered + included image refs sent to eBay (gallery first, cap 12).
  const selectedPictureRefs = useMemo(() => {
    const byId = new Map(photoCatalog.map((i) => [i.id, i]));
    return photoOrder
      .filter((pid) => includedPhotos.has(pid))
      .map((pid) => byId.get(pid)?.ref)
      .filter((ref): ref is string => !!ref)
      .slice(0, 12);
  }, [photoOrder, includedPhotos, photoCatalog]);

  // Deeplink handler: when /live navigates here with ?openGoLive=true and
  // sellSimilar* or relist* params, fire the Go-Live form and pre-fill from
  // the URL, then strip the params so refresh doesn't loop.
  const search = Route.useSearch();
  const deeplinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deeplinkAppliedRef.current) return;
    if (!product) return;
    if (search.openGoLive !== "true") return;
    deeplinkAppliedRef.current = true;
    void (async () => {
      await openGoLiveForm();
      const overrideListingType =
        search.sellSimilarListingType ?? search.relistListingType;
      if (overrideListingType) setGoLiveListingType(overrideListingType);
      if (search.sellSimilarTitle) setGoLiveTitle(search.sellSimilarTitle);
      const overridePrice = search.sellSimilarPrice ?? search.relistAskingPrice;
      if (overridePrice) {
        if ((overrideListingType ?? "Auction") === "Auction") {
          setGoLiveStartingBid(overridePrice);
          userEditedStartingBidRef.current = true;
        } else {
          setGoLivePrice(overridePrice);
        }
      }
      if (search.sellSimilarReservePrice) {
        setGoLiveReservePrice(search.sellSimilarReservePrice);
      }
      if (search.initialSupplyIds) {
        const ids = search.initialSupplyIds.split(",").filter(Boolean);
        if (ids.length > 0) setSelectedSupplyIds(new Set(ids));
      }
      void navigate({
        to: "/products/$id",
        params: { id },
        search: {},
        replace: true,
      });
    })();
  }, [product, search, openGoLiveForm, navigate, id]);

  const applyTemplate = useCallback(
    (tmpl: ListingTemplate) => {
      const firstCard = packagedCards[0];
      const cardName =
        firstCard?.name ?? product?.name ?? product?.productType ?? "";
      const setName = firstCard?.setName ?? "";
      const condition = goLiveCondition;
      const parsedAuction = parseFloat(goLiveStartingBid);
      const parsedFixed = parseFloat(goLivePrice);
      const price =
        goLiveListingType === "Auction" &&
        !isNaN(parsedAuction) &&
        parsedAuction > 0
          ? `$${parsedAuction.toFixed(2)}`
          : !isNaN(parsedFixed) && parsedFixed > 0
            ? `$${parsedFixed.toFixed(2)}`
            : product?.targetSalePrice && product.targetSalePrice > 0
              ? `$${product.targetSalePrice.toFixed(2)}`
              : "";
      // Defer to the shared placeholder resolver — keeps {{generated}} +
      // formatting (whitespace collapse, trim) consistent with the open-form
      // path. Build the auto-description on demand so {{generated}} expands
      // to the same multi-section output as the From Cards button.
      const generated = product
        ? buildDescriptionForProduct(product, packagedCards).plainText
        : "";
      const result = resolveTemplatePlaceholders(tmpl.body, {
        firstCard,
        condition,
        price,
        generated,
      });
      setGoLiveDescription(result.slice(0, 4000));
      setShowTemplatePicker(false);
    },
    [
      packagedCards,
      product,
      goLiveCondition,
      goLiveStartingBid,
      goLivePrice,
      goLiveListingType,
    ],
  );

  const handleGoLive = useCallback(async () => {
    if (!product) return;
    const fieldErrors: Record<string, string> = {};
    if (pushToEbay && goLiveVenue === "eBay") {
      if (goLiveListingType === "Auction") {
        const bid = parseFloat(goLiveStartingBid);
        if (!goLiveStartingBid.trim() || isNaN(bid) || bid <= 0) {
          fieldErrors.startingBid = "Starting bid must be a positive number";
        }
        const reserve = goLiveReservePrice.trim()
          ? parseFloat(goLiveReservePrice)
          : NaN;
        if (goLiveReservePrice.trim() && (isNaN(reserve) || reserve <= 0)) {
          fieldErrors.reservePrice = "Reserve price must be a positive number";
        }
      } else {
        const price = parseFloat(goLivePrice);
        if (!goLivePrice.trim() || isNaN(price) || price <= 0) {
          fieldErrors.price = "Buy It Now price must be a positive number";
        }
      }
    }
    if (Object.keys(fieldErrors).length > 0) {
      setGoLiveFieldErrors(fieldErrors);
      return;
    }
    setGoLiveFieldErrors({});

    const listingTitle =
      goLiveTitle ||
      product.listingTitle ||
      product.name ||
      product.productType;
    const isEbayAuction =
      pushToEbay && goLiveVenue === "eBay" && goLiveListingType === "Auction";
    const askingPrice = isEbayAuction
      ? parseFloat(goLiveStartingBid) || 0
      : parseFloat(goLivePrice) || product.targetSalePrice;

    const listing: LiveListing = {
      id: generateId("LIVE"),
      productId: id,
      title: listingTitle,
      venue: goLiveVenue,
      askingPrice,
      cardCount: packagedCardIds.length,
      startedAt: new Date().toISOString(),
      listingType:
        pushToEbay && goLiveVenue === "eBay" ? goLiveListingType : undefined,
      endsAt: (() => {
        if (!goLiveEndsAt.trim()) return undefined;
        const d = new Date(goLiveEndsAt.trim());
        return isNaN(d.getTime()) ? undefined : d.toISOString();
      })(),
      supplyIds: [...selectedSupplyIds],
    };

    if (pushToEbay && goLiveVenue === "eBay") {
      setEbayPushLoading(true);
      setEbayPushError("");
      setGoLiveUploadProgress("Creating listing…");
      let pushFailed = false;
      const origin = window.location.origin;
      const toAbsolute = (ref: string) =>
        `${origin}/api/images/${encodeURIComponent(ref)}`;
      // User-ordered + selected photos from the picker (gallery first, cap 12).
      const pictureUrls = selectedPictureRefs.map(toAbsolute);
      try {
        const response = await apiFetch(
          "/api/ebay/listings/create-from-product",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              productId: id,
              title: listingTitle,
              price:
                goLiveListingType === "FixedPrice"
                  ? parseFloat(goLivePrice) || 0
                  : 0,
              listingType: goLiveListingType,
              startingBid:
                goLiveListingType === "Auction"
                  ? parseFloat(goLiveStartingBid)
                  : undefined,
              reservePrice:
                goLiveListingType === "Auction" && goLiveReservePrice.trim()
                  ? parseFloat(goLiveReservePrice)
                  : undefined,
              shippingCost: goLiveShipping.trim()
                ? parseFloat(goLiveShipping)
                : undefined,
              conditionLabel: goLiveCondition,
              description: goLiveDescription,
              duration: goLiveDuration === 0.5 ? 1 : goLiveDuration,
              pictureUrls,
            }),
          },
        );
        const contentType = response.headers.get("content-type") ?? "";
        const isJson = contentType.includes("application/json");
        const data = isJson
          ? ((await response.json()) as {
              itemId?: string;
              listingUrl?: string;
              error?: string;
            })
          : { error: await response.text() };
        if (!response.ok) {
          setEbayPushError(data.error ?? "Could not create eBay listing.");
          pushFailed = true;
        } else {
          listing.ebayListingId = data.itemId;
          listing.ebayListingUrl = data.listingUrl;
        }
      } catch (e: unknown) {
        setEbayPushError(
          e instanceof Error ? e.message : "Network error — please try again.",
        );
        pushFailed = true;
      } finally {
        setEbayPushLoading(false);
        setGoLiveUploadProgress(null);
      }
      if (pushFailed) return;
    }

    const newSuppliesTotal = [...selectedSupplyIds].reduce((sum, sid) => {
      const s = shippingSupplies.find((x) => x.id === sid);
      return sum + (s ? supplyCostFor(s, packagedCardIds.length) : 0);
    }, 0);

    goLive(id, listing, newSuppliesTotal > 0 ? newSuppliesTotal : undefined);
    setShowGoLiveForm(false);
  }, [
    product,
    pushToEbay,
    goLiveVenue,
    goLiveListingType,
    goLiveStartingBid,
    goLiveReservePrice,
    goLivePrice,
    goLiveTitle,
    goLiveEndsAt,
    selectedSupplyIds,
    shippingSupplies,
    goLiveShipping,
    goLiveCondition,
    goLiveDescription,
    goLiveDuration,
    id,
    packagedCards,
    packagedCardIds.length,
    panelImageRef,
    compositeImageRef,
    selectedPictureRefs,
    goLive,
  ]);

  const uniqSorted = (vals: Array<string | undefined>) =>
    Array.from(new Set(vals.filter((v): v is string => !!v))).sort();

  const pickerSetOptions = useMemo(
    () => uniqSorted(unlistedCards.map((c) => c.setName)),
    [unlistedCards],
  );
  const pickerRarityOptions = useMemo(
    () => uniqSorted(unlistedCards.map((c) => c.rarity)),
    [unlistedCards],
  );
  const pickerConditionOptions = useMemo(
    () => uniqSorted(unlistedCards.map((c) => c.condition)),
    [unlistedCards],
  );

  const pkgSetOptions = useMemo(
    () => uniqSorted(packagedCards.map((c) => c.setName)),
    [packagedCards],
  );
  const pkgRarityOptions = useMemo(
    () => uniqSorted(packagedCards.map((c) => c.rarity)),
    [packagedCards],
  );
  const pkgConditionOptions = useMemo(
    () => uniqSorted(packagedCards.map((c) => c.condition)),
    [packagedCards],
  );

  const filteredUnlisted = useMemo(() => {
    let base = unlistedCards;
    if (bundlePickerCardIds !== null) {
      base = base.filter((c) => bundlePickerCardIds.has(c.id));
    }
    base = base.filter(
      (c) =>
        (pickerFilterSet === "All" || c.setName === pickerFilterSet) &&
        (pickerFilterRarity === "All" || c.rarity === pickerFilterRarity) &&
        (pickerFilterCondition === "All" ||
          c.condition === pickerFilterCondition),
    );
    if (!pickerSearch.trim()) return base;
    const q = pickerSearch.toLowerCase();
    return base.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.setName.toLowerCase().includes(q) ||
        c.rarity.toLowerCase().includes(q),
    );
  }, [
    unlistedCards,
    pickerSearch,
    bundlePickerCardIds,
    pickerFilterSet,
    pickerFilterRarity,
    pickerFilterCondition,
  ]);

  const filteredPackagedCards = useMemo(
    () =>
      packagedCards.filter(
        (c) =>
          (pkgFilterSet === "All" || c.setName === pkgFilterSet) &&
          (pkgFilterRarity === "All" || c.rarity === pkgFilterRarity) &&
          (pkgFilterCondition === "All" || c.condition === pkgFilterCondition),
      ),
    [packagedCards, pkgFilterSet, pkgFilterRarity, pkgFilterCondition],
  );

  const pkgFilterActive =
    pkgFilterSet !== "All" ||
    pkgFilterRarity !== "All" ||
    pkgFilterCondition !== "All";
  const pickerChipFilterActive =
    pickerFilterSet !== "All" ||
    pickerFilterRarity !== "All" ||
    pickerFilterCondition !== "All";

  const totalCardValue = useMemo(
    () =>
      packagedCards.reduce(
        (sum, c) => sum + (c.marketPrice || c.expectedSalePrice || 0),
        0,
      ),
    [packagedCards],
  );

  const totalCost = (product?.costBasis ?? 0) + (product?.packagingCost ?? 0);
  const profit = (product?.targetSalePrice ?? 0) - totalCost;
  const roi = totalCost > 0 ? (profit / totalCost) * 100 : 0;

  const advanceStatus = () => {
    if (!product) return;
    const idx = STATUS_CYCLE.indexOf(product.status);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    updateProduct({ ...product, status: next });

    // TNK-214 flow: Draft → Ready to List → Listed → Sold → Picked → Packed →
    // Shipped. Cards are held In Package from make-time; pick/pack is post-sale
    // fulfillment (before ship). Each post-Draft state mirrors a Sale-row state
    // so /sales filter tabs light up as the operator cycles, making it possible
    // to verify every UI state from a single product. The per-state branches
    // below key off the status NAME, so they stay correct under this order.
    //
    // Picks the most recent sale whose itemId matches this product; if
    // none exists and we're entering a state that needs one, a minimal
    // "Local" sale is auto-created.
    const nowIso = new Date().toISOString();
    const NEEDS_SALE: ProductStatus[] = [
      "Picked",
      "Packed",
      "Listed",
      "Sold",
      "Shipped",
    ];
    let linkedSale = [...sales]
      .filter((s) => s.itemId === product.id)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

    if (!linkedSale && NEEDS_SALE.includes(next)) {
      const newSale: Sale = {
        id: generateId("SALE"),
        saleDate: nowIso.split("T")[0] ?? "",
        itemType: "Product",
        itemId: product.id,
        itemName: product.name || product.listingTitle || product.id,
        venue: "Local",
        salePrice: product.targetSalePrice ?? 0,
        buyerShipping: 0,
        actualShipping: 0,
        platformFees: 0,
        paymentFees: 0,
        packagingCost: product.packagingCost ?? 0,
        sourceLotId: product.sourceLotId ?? "",
        trackingNumber: "",
        shipped: false,
        packed: false,
        createdAt: nowIso,
      };
      addSale(newSale);
      linkedSale = newSale;
    }
    if (!linkedSale) return;

    // The `null as unknown as undefined` cast lets us send literal nulls for
    // the timestamp columns over the PATCH wire — the server toPatch already
    // accepts `null` to clear them; the cast just satisfies the optional
    // string Sale type without widening it project-wide.
    const nullTs = null as unknown as undefined;
    if (next === "Picked") {
      // Cards pulled from bins. Sale shows under /sales Picked tab.
      updateSale({
        ...linkedSale,
        pickedAt: nowIso,
        packed: false,
        packedAt: nullTs,
        shipped: false,
        shippedAt: nullTs,
      });
    } else if (next === "Packed") {
      // Sleeved + boxed. Sale shows under /sales Packed tab.
      updateSale({
        ...linkedSale,
        pickedAt: linkedSale.pickedAt ?? nowIso,
        packed: true,
        packedAt: nowIso,
        shipped: false,
        shippedAt: nullTs,
      });
    } else if (next === "Listed" || next === "Sold") {
      // Pre-pack: by Listed the product is on the platform; by Sold the
      // buyer has paid. Fulfillment hasn't started for that specific buyer
      // yet — reset the sale to a clean Pending so /sales shows it under
      // the Pending tab.
      updateSale({
        ...linkedSale,
        pickedAt: nullTs,
        packed: false,
        packedAt: nullTs,
        shipped: false,
        shippedAt: nullTs,
      });
    } else if (next === "Shipped") {
      // Out the door. Sale shows under /sales Shipped tab.
      updateSale({
        ...linkedSale,
        pickedAt: linkedSale.pickedAt ?? nowIso,
        packed: true,
        packedAt: linkedSale.packedAt ?? nowIso,
        shipped: true,
        shippedAt: nowIso,
      });
    }
  };

  const handleDelete = () => {
    if (!product) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteProductAndUnpackCards(product.id);
    navigate({ to: "/products" });
  };

  // TNK-102 archive flow. Archive is the day-to-day "remove from view"
  // path; Permanently Delete (handleDelete above) only appears on archived
  // products. The 409-on-active-listings error is surfaced inline so the
  // operator knows which listings to end first.
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const handleArchive = async () => {
    if (!product) return;
    setArchiveError(null);
    const { error } = await archiveProduct(product.id);
    if (error) {
      setArchiveError(error);
    }
  };
  const handleRestore = async () => {
    if (!product) return;
    setArchiveError(null);
    const { error } = await restoreProduct(product.id);
    if (error) {
      setArchiveError(error);
    }
  };

  const toggleSelect = (cardId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const handleAddCards = () => {
    if (selectedIds.size === 0) return;
    addCardsToProduct(id, [...selectedIds]);
    setSelectedIds(new Set());
    setShowPicker(false);
    setPickerSearch("");
    setBundlePickerCardIds(null);
  };

  const openBundlePicker = (suggestion: BundleSuggestion) => {
    const cardIdSet = new Set(suggestion.cards.map((c) => c.id));
    setBundlePickerCardIds(cardIdSet);
    setSelectedIds(new Set(cardIdSet));
    setPickerSearch("");
    setShowPicker(true);
  };

  const handleRemove = (cardId: string) => {
    if (confirmRemoveId !== cardId) {
      setConfirmRemoveId(cardId);
      return;
    }
    removeCardFromProduct(id, cardId);
    setConfirmRemoveId(null);
  };

  const showBulkRefreshBanner = useCallback(
    (banner: { kind: "success" | "error"; message: string }) => {
      if (bulkRefreshBannerTimer.current)
        clearTimeout(bulkRefreshBannerTimer.current);
      setBulkRefreshBanner(banner);
      bulkRefreshBannerTimer.current = setTimeout(() => {
        setBulkRefreshBanner(null);
        bulkRefreshBannerTimer.current = null;
      }, 4000);
    },
    [],
  );

  const fetchCardPriceOnly = useCallback(
    async (
      card: Card,
    ): Promise<
      | {
          kind: "updated";
          price: number;
          source: "TCGplayer" | "eBay Sold";
          ebaySales: EbaySale[];
        }
      | { kind: "no-price"; ebaySales: EbaySale[] }
      | { kind: "error" }
    > => {
      setRefreshingPriceIds((prev) => new Set(prev).add(card.id));
      setNoPriceFoundIds((prev) => {
        const next = new Set(prev);
        next.delete(card.id);
        return next;
      });
      setPriceRefreshErrorIds((prev) => {
        const next = new Set(prev);
        next.delete(card.id);
        return next;
      });
      try {
        // Parallel fetch: TCG market price + eBay sales history. Either source
        // failing is fine — we use whatever responds. Sales history is always
        // useful even when TCG gives us the live price.
        const [tcgResult, ebayResult] = await Promise.allSettled([
          fetchCardMarketPrice(card.setCode, card.cardNumber, card.name),
          fetchEbaySales(card),
        ]);

        const tcgPrice =
          tcgResult.status === "fulfilled" ? tcgResult.value : null;
        const ebay =
          ebayResult.status === "fulfilled"
            ? ebayResult.value
            : { median: null, sales: [] as EbaySale[] };

        const price = tcgPrice ?? ebay.median;
        const source: "TCGplayer" | "eBay Sold" =
          tcgPrice != null ? "TCGplayer" : "eBay Sold";

        if (price != null) {
          return { kind: "updated", price, source, ebaySales: ebay.sales };
        }
        setNoPriceFoundIds((prev) => new Set(prev).add(card.id));
        return { kind: "no-price", ebaySales: ebay.sales };
      } catch {
        setPriceRefreshErrorIds((prev) => new Set(prev).add(card.id));
        return { kind: "error" };
      } finally {
        setRefreshingPriceIds((prev) => {
          const next = new Set(prev);
          next.delete(card.id);
          return next;
        });
      }
    },
    [],
  );

  const handleRefreshPrice = useCallback(
    async (card: Card) => {
      const result = await fetchCardPriceOnly(card);
      if (result.kind === "error") return;
      // Merge real eBay sales into history. Replaces the old behavior of
      // appending the previous marketPrice as a synthetic entry.
      const mergedHistory = mergeEbaySalesIntoHistory(
        card.priceHistory ?? [],
        result.kind === "no-price" ? result.ebaySales : result.ebaySales,
      );
      if (result.kind === "no-price") {
        // No fresh price, but may have appended new sales to history.
        if (mergedHistory.length !== (card.priceHistory?.length ?? 0)) {
          updateCard({ ...card, priceHistory: mergedHistory });
        }
        return;
      }
      updateCard({
        ...card,
        marketPrice: result.price,
        expectedSalePrice: conditionAdjust(result.price, card.condition),
        priceSource: result.source,
        pricedAt: new Date().toISOString(),
        priceHistory: mergedHistory,
      });
    },
    [fetchCardPriceOnly, updateCard],
  );

  const handleRefreshAllPrices = useCallback(async () => {
    if (bulkRefreshing || packagedCards.length === 0) return;
    setBulkRefreshing(true);
    let failed = 0;
    const patches: Array<{
      id: string;
      patch: Partial<Card>;
    }> = [];
    const CONCURRENCY = 4;
    const queue = [...packagedCards];
    const nowIso = new Date().toISOString();
    try {
      const worker = async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (!next) return;
          const result = await fetchCardPriceOnly(next);
          if (result.kind === "error") {
            failed += 1;
            continue;
          }
          const mergedHistory = mergeEbaySalesIntoHistory(
            next.priceHistory ?? [],
            result.ebaySales,
          );
          if (result.kind === "updated") {
            patches.push({
              id: next.id,
              patch: {
                marketPrice: result.price,
                priceSource: result.source,
                pricedAt: nowIso,
                expectedSalePrice: conditionAdjust(
                  result.price,
                  next.condition,
                ),
                priceHistory: mergedHistory,
              },
            });
          } else if (
            mergedHistory.length !== (next.priceHistory?.length ?? 0)
          ) {
            // No fresh price but new sales arrived — patch history only.
            patches.push({
              id: next.id,
              patch: { priceHistory: mergedHistory },
            });
          } else {
            failed += 1;
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(CONCURRENCY, packagedCards.length) },
          worker,
        ),
      );
      if (patches.length > 0) bulkUpdateCards(patches);
      const updated = patches.length;
      if (failed > 0) {
        showBulkRefreshBanner({
          kind: "error",
          message:
            updated > 0
              ? `Refreshed ${updated} card${updated === 1 ? "" : "s"} · ${failed} failed`
              : `Failed to refresh ${failed} card${failed === 1 ? "" : "s"}`,
        });
      } else {
        showBulkRefreshBanner({
          kind: "success",
          message: `Refreshed ${updated} card${updated === 1 ? "" : "s"}`,
        });
      }
    } finally {
      setBulkRefreshing(false);
    }
  }, [
    bulkRefreshing,
    packagedCards,
    fetchCardPriceOnly,
    bulkUpdateCards,
    showBulkRefreshBanner,
  ]);

  const handleLogPrice = useCallback(
    (card: Card) => {
      const parsed = parseFloat(logPriceValue);
      if (!logPriceValue.trim() || isNaN(parsed) || parsed < 0) {
        setLogPriceError("Enter a valid price");
        return;
      }
      if (
        logPriceDate.trim() &&
        isNaN(new Date(logPriceDate.trim()).getTime())
      ) {
        setLogPriceError("Enter a valid date (e.g. 2025-04-30)");
        return;
      }
      const dateStr = logPriceDate.trim()
        ? new Date(logPriceDate.trim()).toISOString()
        : new Date().toISOString();
      const entry: PriceHistoryEntry = {
        price: parsed,
        source: logPriceSource,
        recordedAt: dateStr,
      };
      const prevHistory: PriceHistoryEntry[] = card.priceHistory ?? [];
      const newHistory = [...prevHistory, entry].slice(-10);
      updateCard({ ...card, priceHistory: newHistory });
      setLogPriceCardId(null);
      setLogPriceValue("");
      setLogPriceSource("Manual");
      setLogPriceDate("");
      setLogPriceError("");
    },
    [logPriceValue, logPriceSource, logPriceDate, updateCard],
  );

  if (!product) {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center">
        <p className="text-sm text-muted-foreground">Product not found</p>
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>
      </section>
    );
  }

  const isLiveEnded =
    !!existingLiveListing?.endsAt &&
    new Date(existingLiveListing.endsAt).getTime() <= now;

  const isArchived = !!product.archivedAt;

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <BackButton fallback={{ to: "/products" }} />
        {isArchived ? (
          // Archived products show Restore + Permanently Delete in the
          // header. Permanently Delete still uses the existing confirm flow.
          confirmDelete ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded-md bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90"
              >
                Permanently delete
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleRestore()}
                className="rounded-md bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20"
              >
                Restore
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label="Permanently delete product"
                className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-destructive hover:bg-muted/80"
                title="Permanently delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )
        ) : (
          // Active products only expose the soft-delete Archive action; the
          // destructive Permanently Delete is gated behind the Archived view.
          <button
            type="button"
            onClick={() => void handleArchive()}
            aria-label="Archive product"
            className="flex h-9 items-center gap-1.5 rounded-md bg-amber-500/15 px-3 text-xs font-semibold text-amber-700 hover:bg-amber-500/25"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Archive
          </button>
        )}
      </div>

      {(isArchived || archiveError) && (
        <div
          className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
            archiveError
              ? "border-destructive bg-destructive/10 text-destructive"
              : "border-amber-300 bg-amber-100 text-amber-800"
          }`}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="flex-1">
            {archiveError ? (
              archiveError
            ) : (
              <>
                Archived
                {product.archivedAt
                  ? ` on ${new Date(product.archivedAt).toLocaleString()}`
                  : ""}
                . Cards stay reserved (In Package) — Restore to bring this
                bundle back fully intact.
              </>
            )}
          </div>
          {archiveError && (
            <button
              type="button"
              onClick={() => setArchiveError(null)}
              aria-label="Dismiss"
              className="text-inherit hover:opacity-70"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
              {product.id}
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {product.name || product.productType}
            </h1>
            <div className="text-xs text-muted-foreground">
              {product.productType}
            </div>
          </div>
          <Badge
            label={product.status}
            variant={productStatusBadge(product.status)}
          />
        </div>

        {/* Imported-shell banner (TNK-112). Imported listings start with no
            local cards linked — eBay's listing is the source of truth for
            what's in the bundle, but we don't know which of the operator's
            inventory cards correspond to it. Explain the state and point
            at Studio for manually linking cards. */}
        {product.productType === "Imported" && packagedCardIds.length === 0 && (
          <div className="rounded-md border border-amber-600/40 bg-amber-100/60 px-3 py-2 text-xs text-amber-800">
            <div className="font-semibold">
              Imported from eBay listing — no local cards linked
            </div>
            <div className="mt-0.5 text-amber-700">
              The eBay listing carries the truth for what's inside this bundle.
              Add cards via Studio if you want to track inventory depletion
              through this product; otherwise leave as-is and the product just
              tracks sales / financials.
            </div>
          </div>
        )}

        {/* Stats card */}
        <div className="rounded-md bg-primary p-4 text-primary-foreground">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatItem label="Cards" value={String(packagedCardIds.length)} />
            <StatItem
              label="Card Value"
              value={`$${totalCardValue.toFixed(2)}`}
              color="text-secondary"
            />
            <StatItem label="Cost" value={`$${totalCost.toFixed(2)}`} />
            <StatItem
              label="Profit"
              value={`$${profit.toFixed(2)}`}
              color={profit >= 0 ? "text-emerald-400" : "text-red-400"}
            />
          </div>
          <div className="mt-3 border-t border-primary-foreground/20 pt-2 text-xs opacity-75">
            Target: ${product.targetSalePrice.toFixed(2)} · ROI:{" "}
            {roi.toFixed(0)}%
            {product.storageLocation ? ` · 📦 ${product.storageLocation}` : ""}
          </div>
        </div>

        {/* Section header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setPkgListExpanded((v) => !v)}
            aria-expanded={pkgListExpanded}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary"
          >
            {pkgListExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            Cards in Package ({packagedCardIds.length})
          </button>
          <div className="flex items-center gap-2">
            {packagedCards.length > 0 && (
              <button
                type="button"
                onClick={handleRefreshAllPrices}
                disabled={bulkRefreshing}
                className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60"
              >
                {bulkRefreshing ? (
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                {bulkRefreshing ? "Refreshing…" : "Refresh all prices"}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setShowPicker((v) => {
                  if (v) {
                    setBundlePickerCardIds(null);
                    setSelectedIds(new Set());
                    setPickerSearch("");
                  }
                  return !v;
                });
              }}
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-semibold ${
                showPicker
                  ? "bg-muted text-muted-foreground hover:text-foreground"
                  : "bg-secondary text-secondary-foreground hover:opacity-90"
              }`}
            >
              {showPicker ? (
                <X className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              {showPicker ? "Cancel" : "Add Cards"}
            </button>
          </div>
        </div>

        {bulkRefreshBanner && (
          <div
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
              bulkRefreshBanner.kind === "success"
                ? "border-emerald-600 bg-emerald-100 text-emerald-700"
                : "border-destructive bg-destructive/10 text-destructive"
            }`}
          >
            {bulkRefreshBanner.kind === "success" ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            {bulkRefreshBanner.message}
          </div>
        )}

        {/* Picker */}
        {showPicker && (
          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {bundlePickerCardIds !== null
                    ? `Bundle Cards (${filteredUnlisted.length})`
                    : `Unlisted Inventory (${unlistedCards.length})`}
                </div>
                {bundlePickerCardIds !== null && (
                  <div className="text-[11px] text-muted-foreground">
                    Showing cards from this bundle suggestion
                  </div>
                )}
              </div>
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={handleAddCards}
                  className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                >
                  Add {selectedIds.size} Card{selectedIds.size !== 1 ? "s" : ""}
                </button>
              )}
            </div>

            <div className="mt-2 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search by name, set, rarity..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                autoComplete="off"
              />
              {pickerSearch.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPickerSearch("")}
                  aria-label="Clear search"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {(pickerSetOptions.length > 0 ||
              pickerRarityOptions.length > 0 ||
              pickerConditionOptions.length > 0) && (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                {pickerSetOptions.length > 0 && (
                  <FilterSelect
                    label="Set"
                    value={pickerFilterSet}
                    options={pickerSetOptions}
                    onChange={setPickerFilterSet}
                  />
                )}
                {pickerRarityOptions.length > 0 && (
                  <FilterSelect
                    label="Rarity"
                    value={pickerFilterRarity}
                    options={pickerRarityOptions}
                    onChange={setPickerFilterRarity}
                  />
                )}
                {pickerConditionOptions.length > 0 && (
                  <FilterSelect
                    label="Condition"
                    value={pickerFilterCondition}
                    options={pickerConditionOptions}
                    onChange={setPickerFilterCondition}
                  />
                )}
                {pickerChipFilterActive && (
                  <button
                    type="button"
                    onClick={() => {
                      setPickerFilterSet("All");
                      setPickerFilterRarity("All");
                      setPickerFilterCondition("All");
                    }}
                    className="inline-flex items-center gap-1 self-end rounded-md border border-border bg-muted px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-card hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                    Reset
                  </button>
                )}
              </div>
            )}

            {filteredUnlisted.length === 0 ? (
              <p className="mt-3 py-3 text-center text-xs text-muted-foreground">
                {unlistedCards.length === 0
                  ? "No unlisted cards in inventory"
                  : "No cards match your search"}
              </p>
            ) : (
              <ul className="mt-2 max-h-80 space-y-1.5 overflow-y-auto">
                {filteredUnlisted.map((card) => {
                  const selected = selectedIds.has(card.id);
                  const value = card.marketPrice || card.expectedSalePrice || 0;
                  return (
                    <li key={card.id}>
                      <button
                        type="button"
                        onClick={() => toggleSelect(card.id)}
                        className={`flex w-full items-center gap-3 rounded-md border p-2 text-left transition-colors ${
                          selected
                            ? "border-primary bg-primary/10"
                            : "border-border bg-card hover:bg-card/80"
                        }`}
                      >
                        <span
                          className={`flex h-4 w-4 items-center justify-center rounded border ${
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-transparent"
                          }`}
                        >
                          {selected && <Check className="h-3 w-3" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-foreground">
                            {card.name}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {card.setName} · {card.rarity} · {card.condition}
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-foreground">
                          ${value.toFixed(2)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* In-package filter chips + list, collapsible. Set chip always shows
            so it's quick to scope down even on a 1-set package; Rarity and
            Condition only surface once there's variety to filter. */}
        {pkgListExpanded && packagedCards.length > 0 && (
          <div className="flex flex-wrap items-end gap-2">
            {pkgSetOptions.length > 0 && (
              <FilterSelect
                label="Set"
                value={pkgFilterSet}
                options={pkgSetOptions}
                onChange={setPkgFilterSet}
              />
            )}
            {pkgRarityOptions.length > 1 && (
              <FilterSelect
                label="Rarity"
                value={pkgFilterRarity}
                options={pkgRarityOptions}
                onChange={setPkgFilterRarity}
              />
            )}
            {pkgConditionOptions.length > 1 && (
              <FilterSelect
                label="Condition"
                value={pkgFilterCondition}
                options={pkgConditionOptions}
                onChange={setPkgFilterCondition}
              />
            )}
            {pkgFilterActive && (
              <button
                type="button"
                onClick={() => {
                  setPkgFilterSet("All");
                  setPkgFilterRarity("All");
                  setPkgFilterCondition("All");
                }}
                className="inline-flex items-center gap-1 self-end rounded-md border border-border bg-muted px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-card hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
            {pkgFilterActive && (
              <span className="self-end pb-1.5 text-[11px] text-muted-foreground">
                {filteredPackagedCards.length} of {packagedCards.length}
              </span>
            )}
          </div>
        )}

        {/* Packaged cards list / empty state */}
        {packagedCards.length === 0 && !showPicker ? (
          <div className="flex flex-col items-center gap-2 rounded-md bg-muted py-8 text-center">
            <Layers className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">
              No cards added yet
            </p>
            <p className="text-xs text-muted-foreground">
              Tap "Add Cards" to pick cards from your unlisted inventory
            </p>
          </div>
        ) : !pkgListExpanded ? null : filteredPackagedCards.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground">
            No cards match the selected filters.
          </p>
        ) : (
          <ul className="space-y-2">
            {filteredPackagedCards.map((card) => (
              <PackagedCardRow
                key={card.id}
                card={card}
                confirming={confirmRemoveId === card.id}
                refreshing={refreshingPriceIds.has(card.id)}
                noPriceFound={noPriceFoundIds.has(card.id)}
                refreshError={priceRefreshErrorIds.has(card.id)}
                historyExpanded={expandedHistoryIds.has(card.id)}
                logPriceOpen={logPriceCardId === card.id}
                logPriceValue={logPriceValue}
                logPriceSource={logPriceSource}
                logPriceDate={logPriceDate}
                logPriceError={logPriceError}
                onToggleHistory={() =>
                  setExpandedHistoryIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(card.id)) next.delete(card.id);
                    else next.add(card.id);
                    return next;
                  })
                }
                onToggleLog={() => {
                  if (logPriceCardId === card.id) {
                    setLogPriceCardId(null);
                    setLogPriceError("");
                  } else {
                    setLogPriceCardId(card.id);
                    setLogPriceValue("");
                    setLogPriceSource("Manual");
                    setLogPriceDate("");
                    setLogPriceError("");
                  }
                }}
                onLogValueChange={(v) => {
                  setLogPriceValue(v);
                  setLogPriceError("");
                }}
                onLogSourceChange={setLogPriceSource}
                onLogDateChange={(v) => {
                  setLogPriceDate(v);
                  setLogPriceError("");
                }}
                onLogSave={() => handleLogPrice(card)}
                onRefreshPrice={() => handleRefreshPrice(card)}
                onRemove={() => handleRemove(card.id)}
                onCancelRemove={() => setConfirmRemoveId(null)}
              />
            ))}
          </ul>
        )}

        {/* Bundle suggestions */}
        {bundleSettings.globalEnabled &&
          !["Sold", "Picked", "Packed", "Shipped"].includes(product.status) && (
            <div className="rounded-md border border-primary/55 bg-card">
              <button
                type="button"
                onClick={() => setBundleSuggestionsExpanded((v) => !v)}
                aria-expanded={bundleSuggestionsExpanded}
                className="flex w-full items-center gap-2 p-3 text-left hover:bg-card/60"
              >
                <Layers className="h-3.5 w-3.5 text-primary" />
                <span className="text-sm font-semibold text-foreground">
                  More Cards to Add
                </span>
                <span className="rounded-md bg-primary/20 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                  {bundleSuggestions.length}
                </span>
                <span className="ml-auto text-muted-foreground">
                  {bundleSuggestionsExpanded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </span>
              </button>
              {bundleSuggestionsExpanded && (
                <p className="px-3 pb-2 text-[11px] text-muted-foreground">
                  Unlisted cards that match bundle patterns — tap to add to this
                  product
                </p>
              )}

              {bundleSuggestionsExpanded && bundleSuggestions.length === 0 && (
                <div className="flex flex-col items-center gap-2 border-t border-border p-5 text-center">
                  <Layers className="h-5 w-5 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    No bundle suggestions for the unlisted inventory yet. Add
                    more cards or relax the bundle thresholds.
                  </p>
                </div>
              )}

              {bundleSuggestionsExpanded &&
                (bundleSuggestionsShowAll
                  ? bundleSuggestions
                  : bundleSuggestions.slice(0, 5)
                ).map((suggestion) => {
                  const gameLabel =
                    suggestion.game === "Pokemon"
                      ? "Pokémon"
                      : suggestion.game === "Magic"
                        ? "MTG"
                        : suggestion.game === "Yu-Gi-Oh"
                          ? "Yu-Gi-Oh!"
                          : suggestion.game;
                  const profitPositive = suggestion.expectedProfit >= 0;
                  return (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => openBundlePicker(suggestion)}
                      className="flex w-full items-start gap-2 border-t border-border p-3 text-left hover:bg-card/60"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded-md bg-secondary/20 px-1.5 py-0.5 text-[10px] font-semibold text-secondary">
                            {gameLabel}
                          </span>
                          <span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                            {suggestion.bundleType}
                          </span>
                          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {suggestion.confidenceScore}%
                          </span>
                        </div>
                        <div className="truncate text-sm font-semibold text-foreground">
                          {suggestion.label}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {suggestion.cardCount} cards · $
                          {suggestion.estimatedMarketValue.toFixed(2)} market
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                              profitPositive
                                ? "bg-emerald-500/15 text-emerald-600"
                                : "bg-destructive/15 text-destructive"
                            }`}
                          >
                            Est profit ${suggestion.expectedProfit.toFixed(2)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <TrendingUp className="h-2.5 w-2.5" />
                            Speed:{" "}
                            {suggestion.sellSpeed === "Unknown"
                              ? "Unknown"
                              : suggestion.sellSpeed}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <Shield className="h-2.5 w-2.5" />
                            Conf: {suggestion.confidence}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="mt-1 h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}

              {bundleSuggestionsExpanded && bundleSuggestions.length > 5 && (
                <button
                  type="button"
                  onClick={() => setBundleSuggestionsShowAll((v) => !v)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-border p-2.5 text-[11px] font-medium text-primary"
                >
                  {bundleSuggestionsShowAll ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                  {bundleSuggestionsShowAll
                    ? "Show top 5 only"
                    : `Show all ${bundleSuggestions.length} suggestions`}
                </button>
              )}
            </div>
          )}

        {/* Live listing readout (Relist/End/Sell-similar coming in E2c) */}
        {existingLiveListing && !showGoLiveForm && (
          <div className="rounded-md border border-border bg-card p-4">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
                LIVE LISTING
              </div>
              {isLiveEnded && <Badge label="Ended" variant="warning" />}
            </div>
            <div className="text-sm font-semibold text-foreground">
              {existingLiveListing.title}
            </div>
            <div className="text-xs text-muted-foreground">
              {existingLiveListing.venue} · $
              {existingLiveListing.askingPrice.toFixed(2)}
              {existingLiveListing.listingType
                ? ` · ${existingLiveListing.listingType}`
                : ""}
            </div>
            {existingLiveListing.ebayListingUrl && (
              <a
                href={existingLiveListing.ebayListingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"
              >
                <ExternalLink className="h-2.5 w-2.5" />
                View on eBay
              </a>
            )}
          </div>
        )}

        {/* Packaging supplies on existing live listing — view + edit */}
        {existingLiveListing && !showGoLiveForm && (
          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-foreground">
                Packaging Supplies
              </div>
              {!showEditSupplies && (
                <button
                  type="button"
                  onClick={() => {
                    setEditSupplyIds(
                      new Set(existingLiveListing.supplyIds ?? []),
                    );
                    setShowEditSupplies(true);
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </button>
              )}
            </div>
            <div className="my-3 h-px bg-border" />
            {showEditSupplies ? (
              <>
                <p className="mb-2 text-xs text-muted-foreground">
                  Toggle supplies to add or remove them. Changes update
                  packaging cost.
                </p>
                {(() => {
                  // Show enabled supplies + anything already on the listing
                  // so a disabled supply can still be removed.
                  const visible = shippingSupplies.filter(
                    (s) => s.enabled || editSupplyIds.has(s.id),
                  );
                  return visible.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No supplies configured. Add them in Tools → Supplies.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {visible.map((supply) => {
                        const selected = editSupplyIds.has(supply.id);
                        return (
                          <button
                            key={supply.id}
                            type="button"
                            onClick={() =>
                              setEditSupplyIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(supply.id)) next.delete(supply.id);
                                else next.add(supply.id);
                                return next;
                              })
                            }
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${
                              selected
                                ? "bg-red-500 text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {selected && <Check className="h-3 w-3" />}
                            {supply.name} ($
                            {supplyCostFor(
                              supply,
                              packagedCardIds.length,
                            ).toFixed(2)}
                            )
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                <p className="mt-2 mb-3 text-xs text-muted-foreground">
                  New total: $
                  {[...editSupplyIds]
                    .reduce((sum, sid) => {
                      const s = shippingSupplies.find((x) => x.id === sid);
                      return (
                        sum + (s ? supplyCostFor(s, packagedCardIds.length) : 0)
                      );
                    }, 0)
                    .toFixed(2)}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditSupplies(false)}
                    className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold text-muted-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      updateListingSupplies(existingLiveListing.id, [
                        ...editSupplyIds,
                      ]);
                      setShowEditSupplies(false);
                    }}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Save Changes
                  </button>
                </div>
              </>
            ) : (
              <>
                {shippingSupplies.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No supplies configured. Add them in Tools → Supplies.
                  </p>
                ) : (existingLiveListing.supplyIds ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No supplies selected for this listing.
                  </p>
                ) : (
                  <>
                    <div className="mb-2 flex flex-wrap gap-2">
                      {(existingLiveListing.supplyIds ?? []).map((sid) => {
                        const supply = shippingSupplies.find(
                          (s) => s.id === sid,
                        );
                        if (!supply) return null;
                        return (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 rounded-md bg-primary/20 px-2 py-1 text-xs font-medium text-primary"
                          >
                            <Package className="h-3 w-3" />
                            {supply.name} ($
                            {supplyCostFor(
                              supply,
                              existingLiveListing.cardCount ??
                                packagedCardIds.length,
                            ).toFixed(2)}
                            )
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Total: $
                      {(existingLiveListing.supplyIds ?? [])
                        .reduce((sum, sid) => {
                          const s = shippingSupplies.find((x) => x.id === sid);
                          const cc =
                            existingLiveListing.cardCount ??
                            packagedCardIds.length;
                          return sum + (s ? supplyCostFor(s, cc) : 0);
                        }, 0)
                        .toFixed(2)}
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Go-Live form */}
        {showGoLiveForm && (
          <div className="space-y-3 rounded-md border-2 border-red-500 bg-card p-4">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-semibold text-foreground">Go Live</h3>
            </div>

            {/* Venue */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                VENUE
              </label>
              <div className="flex flex-wrap gap-1.5">
                {VENUES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setGoLiveVenue(v);
                      if (v !== "eBay") setPushToEbay(false);
                      else void checkEbayStatus();
                    }}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                      goLiveVenue === v
                        ? "bg-red-500 text-white"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                LISTING TITLE
              </label>
              <input
                type="text"
                value={goLiveTitle}
                onChange={(e) => setGoLiveTitle(e.target.value)}
                placeholder="Listing title…"
                className="w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/40"
              />
            </div>

            {/* Listing type */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                LISTING TYPE
              </label>
              <div className="flex flex-wrap gap-1.5">
                {(["FixedPrice", "Auction"] as const).map((lt) => (
                  <button
                    key={lt}
                    type="button"
                    onClick={() => {
                      setGoLiveListingType(lt);
                      if (lt !== goLiveListingType)
                        setGoLiveListingTypeReason(null);
                      setGoLiveFieldErrors({});
                      handleDurationChange(lt === "FixedPrice" ? "GTC" : 7);
                    }}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                      goLiveListingType === lt
                        ? "bg-red-500 text-white"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {lt === "FixedPrice" ? "Fixed Price" : "Auction"}
                  </button>
                ))}
              </div>
              {goLiveListingTypeReason && (
                <p className="text-[11px] text-muted-foreground">
                  {goLiveListingTypeReason}
                </p>
              )}
            </div>

            {/* Price (Fixed Price) */}
            {goLiveListingType === "FixedPrice" && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                  {pushToEbay && goLiveVenue === "eBay"
                    ? "BUY IT NOW PRICE ($)"
                    : "ASKING PRICE ($)"}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={goLivePrice}
                  onChange={(e) => {
                    setGoLivePrice(e.target.value);
                    setGoLiveFieldErrors((err) => ({ ...err, price: "" }));
                  }}
                  placeholder="0.00"
                  className={`w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
                    goLiveFieldErrors.price
                      ? "ring-2 ring-red-500"
                      : "focus:ring-red-500/40"
                  }`}
                />
                {goLiveFieldErrors.price && (
                  <p className="text-[11px] text-red-500">
                    {goLiveFieldErrors.price}
                  </p>
                )}
              </div>
            )}

            {/* Starting bid (Auction) */}
            {goLiveListingType === "Auction" && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                  STARTING BID ($) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={goLiveStartingBid}
                  onChange={(e) => {
                    userEditedStartingBidRef.current = true;
                    setGoLiveStartingBid(e.target.value);
                    setGoLiveFieldErrors((err) => ({
                      ...err,
                      startingBid: "",
                    }));
                  }}
                  placeholder="0.00"
                  className={`w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
                    goLiveFieldErrors.startingBid
                      ? "ring-2 ring-red-500"
                      : "focus:ring-red-500/40"
                  }`}
                />
                {goLiveFieldErrors.startingBid ? (
                  <p className="text-[11px] text-red-500">
                    {goLiveFieldErrors.startingBid}
                  </p>
                ) : product.targetSalePrice > 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    {describeDemandSuggestion(
                      demandSuggestion,
                      product.targetSalePrice,
                    )}
                  </p>
                ) : null}
              </div>
            )}

            {/* Duration */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                LISTING DURATION
              </label>
              <div className="flex flex-wrap gap-1.5">
                {(goLiveListingType === "Auction"
                  ? ([0.5, 1, 3, 5, 7, 10] as const)
                  : ([1, 7, 10, 30, "GTC"] as const)
                ).map((d) => (
                  <button
                    key={String(d)}
                    type="button"
                    onClick={() => handleDurationChange(d as GoLiveDuration)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                      goLiveDuration === d
                        ? "bg-red-500 text-white"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {d === "GTC" ? "GTC" : d === 0.5 ? "12h" : `${d}d`}
                  </button>
                ))}
              </div>
              {pushToEbay &&
                goLiveVenue === "eBay" &&
                goLiveDuration === 0.5 && (
                  <p className="text-[11px] text-muted-foreground">
                    12h is local-only — eBay minimum is 1 day.
                  </p>
                )}
            </div>

            {/* eBay status + push toggle */}
            {goLiveVenue === "eBay" &&
              (ebayStatus === "loading" ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Checking eBay connection…
                </div>
              ) : (
                <>
                  {ebayStatus === "disconnected" && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <div className="flex-1 space-y-0.5">
                        <p className="text-xs font-semibold text-amber-700">
                          eBay is not connected
                        </p>
                        <p className="text-[11px] text-amber-700/80">
                          Connect your eBay account to enable pushing listings
                          live.
                        </p>
                      </div>
                      <Link
                        to="/tools/ebay"
                        onClick={() => setShowGoLiveForm(false)}
                        className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-500 hover:underline"
                      >
                        Go to settings
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  )}
                  <label
                    className={`flex items-start justify-between gap-3 ${
                      ebayStatus === "disconnected" ? "opacity-45" : ""
                    }`}
                  >
                    <div className="space-y-0.5">
                      <span className="block text-[10px] font-medium tracking-wider text-muted-foreground">
                        PUSH TO EBAY
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        Create a live eBay listing immediately
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={pushToEbay}
                      disabled={ebayStatus === "disconnected"}
                      onChange={(e) => setPushToEbay(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-red-500"
                    />
                  </label>
                  {ebayPushError && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                      <p className="text-xs text-destructive">
                        {ebayPushError}
                      </p>
                    </div>
                  )}

                  {pushToEbay && (
                    <>
                      {goLiveListingType === "Auction" && (
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                            RESERVE PRICE ($){" "}
                            <span className="text-muted-foreground">
                              (optional)
                            </span>
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={goLiveReservePrice}
                            onChange={(e) => {
                              setGoLiveReservePrice(e.target.value);
                              setGoLiveFieldErrors((err) => ({
                                ...err,
                                reservePrice: "",
                              }));
                            }}
                            placeholder="0.00"
                            className={`w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
                              goLiveFieldErrors.reservePrice
                                ? "ring-2 ring-red-500"
                                : "focus:ring-red-500/40"
                            }`}
                          />
                          {goLiveFieldErrors.reservePrice && (
                            <p className="text-[11px] text-red-500">
                              {goLiveFieldErrors.reservePrice}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Condition */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                          CONDITION
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {(["NM", "LP", "MP", "HP", "Damaged"] as const).map(
                            (cond) => (
                              <button
                                key={cond}
                                type="button"
                                onClick={() => setGoLiveCondition(cond)}
                                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                                  goLiveCondition === cond
                                    ? "bg-red-500 text-white"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {cond}
                              </button>
                            ),
                          )}
                        </div>
                      </div>

                      {/* Shipping */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                          SHIPPING COST ($)
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={goLiveShipping}
                          onChange={(e) => setGoLiveShipping(e.target.value)}
                          placeholder="1.10"
                          className="w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/40"
                        />
                      </div>

                      {/* Packaging supplies */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                          PACKAGING SUPPLIES
                        </label>
                        {shippingSupplies.length === 0 ? (
                          <Link
                            to="/products"
                            onClick={() => setShowGoLiveForm(false)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"
                          >
                            No supplies configured — add in Tools → Supplies
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <>
                            <p className="text-[11px] text-muted-foreground">
                              Selected costs added to packaging cost on go-live.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {shippingSupplies
                                .filter(
                                  (supply) =>
                                    supply.enabled ||
                                    selectedSupplyIds.has(supply.id),
                                )
                                .map((supply) => {
                                  const selected = selectedSupplyIds.has(
                                    supply.id,
                                  );
                                  return (
                                    <button
                                      key={supply.id}
                                      type="button"
                                      onClick={() =>
                                        setSelectedSupplyIds((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(supply.id))
                                            next.delete(supply.id);
                                          else next.add(supply.id);
                                          return next;
                                        })
                                      }
                                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${
                                        selected
                                          ? "bg-red-500 text-white"
                                          : "bg-muted text-muted-foreground"
                                      }`}
                                    >
                                      {selected && (
                                        <Check className="h-3 w-3" />
                                      )}
                                      {supply.name} ($
                                      {supplyCostFor(
                                        supply,
                                        packagedCardIds.length,
                                      ).toFixed(2)}
                                      )
                                    </button>
                                  );
                                })}
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              Total: $
                              {[...selectedSupplyIds]
                                .reduce((s, sid) => {
                                  const sup = shippingSupplies.find(
                                    (x) => x.id === sid,
                                  );
                                  return (
                                    s +
                                    (sup
                                      ? supplyCostFor(
                                          sup,
                                          packagedCardIds.length,
                                        )
                                      : 0)
                                  );
                                }, 0)
                                .toFixed(2)}
                            </p>
                          </>
                        )}
                      </div>

                      {/* Description */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                            DESCRIPTION
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground">
                              {goLiveDescription.length}/4000
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const next = buildDescriptionForProduct(
                                  product,
                                  packagedCards,
                                );
                                setGeneratedDescription(next);
                                setGoLiveDescription(
                                  next.plainText.slice(0, 4000),
                                );
                              }}
                              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                              <RefreshCw className="h-3 w-3" />
                              From Cards
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowTemplatePicker((v) => !v)}
                              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold ${
                                showTemplatePicker
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <FileText className="h-3 w-3" />
                              Use Template
                            </button>
                          </div>
                        </div>

                        {showTemplatePicker && (
                          <div className="overflow-hidden rounded-md border border-border bg-card">
                            {listingTemplates.length === 0 ? (
                              <p className="p-3 text-center text-xs text-muted-foreground">
                                No templates yet. Create one in Tools →
                                Templates.
                              </p>
                            ) : (
                              listingTemplates.map((tmpl, idx) => (
                                <button
                                  key={tmpl.id}
                                  type="button"
                                  onClick={() => applyTemplate(tmpl)}
                                  className={`w-full space-y-0.5 px-3 py-2 text-left hover:bg-muted ${
                                    idx === 0 ? "" : "border-t border-border"
                                  }`}
                                >
                                  <div className="text-sm font-semibold text-foreground">
                                    {tmpl.name}
                                  </div>
                                  <div className="line-clamp-2 text-[11px] text-muted-foreground">
                                    {tmpl.body || "(empty body)"}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}

                        {generatedDescription &&
                          goLiveDescription ===
                            generatedDescription.plainText && (
                            <div className="space-y-2 rounded-md border border-border bg-card p-3">
                              <p className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                                GENERATED FROM YOUR CARDS
                              </p>
                              {generatedDescription.sections.map(
                                (section, idx) => (
                                  <div key={idx} className="space-y-0.5">
                                    {section.heading && (
                                      <p className="text-xs font-semibold text-foreground">
                                        {section.heading}
                                      </p>
                                    )}
                                    {section.kind === "paragraph" ? (
                                      <p className="text-[11px] leading-snug text-muted-foreground">
                                        {section.text}
                                      </p>
                                    ) : (
                                      section.items.map((item, i) => (
                                        <p
                                          key={i}
                                          className="text-[11px] leading-snug text-muted-foreground"
                                        >
                                          • {item}
                                        </p>
                                      ))
                                    )}
                                  </div>
                                ),
                              )}
                              <p className="text-[11px] italic text-muted-foreground">
                                Edit below to customize before listing.
                              </p>
                            </div>
                          )}

                        <textarea
                          value={goLiveDescription}
                          onChange={(e) =>
                            setGoLiveDescription(e.target.value.slice(0, 4000))
                          }
                          placeholder="Describe your listing…"
                          rows={5}
                          className="w-full resize-y rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/40"
                        />
                      </div>

                      {/* Listing photos: optional stitched bundle panel + per-card front photos */}
                      {(() => {
                        const withPhotos = packagedCards.filter(
                          (c) => c.imageFrontRef,
                        ).length;
                        const totalCards = packagedCards.length;
                        const canPanel = withPhotos >= 4;
                        const previewRef = compositeImageRef ?? panelImageRef;
                        const panelUrl = previewRef
                          ? imageUrl(previewRef)
                          : null;
                        const watermarkAvailable = !!storeImages.watermark;
                        const logoAvailable = !!storeImages.logo;
                        return (
                          <div className="space-y-2 rounded-md border border-border bg-card/50 p-3">
                            <div className="flex items-center justify-between gap-2">
                              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                                LISTING PHOTOS
                              </label>
                              {canPanel && (
                                <button
                                  type="button"
                                  onClick={() => void generateBundlePanel()}
                                  disabled={panelGenerating}
                                  className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
                                >
                                  {panelGenerating ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Images className="h-3 w-3" />
                                  )}
                                  {panelGenerating
                                    ? "Stitching…"
                                    : panelImageRef
                                      ? "Regenerate panel"
                                      : "Generate bundle panel"}
                                </button>
                              )}
                            </div>
                            {panelUrl && (
                              <div className="space-y-1">
                                <img
                                  src={panelUrl}
                                  alt="Bundle panel preview"
                                  className="w-full max-w-sm rounded border border-border"
                                />
                                <div className="flex items-center gap-2">
                                  <p className="text-[11px] text-muted-foreground">
                                    Used as the gallery image; per-card photos
                                    fill the remaining slots (eBay caps at 12).
                                  </p>
                                  <button
                                    type="button"
                                    onClick={removeBundlePanel}
                                    className="text-[11px] font-medium text-destructive hover:underline"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                            {!panelUrl && (
                              <p className="text-[11px] text-muted-foreground">
                                {canPanel
                                  ? "Click Generate to compose a stitched panel of your packaged cards."
                                  : totalCards >= 4
                                    ? `Need at least 4 cards with front photos to compose a panel (${withPhotos}/${totalCards}). Per-card photos will still upload.`
                                    : "Need at least 4 packaged cards to compose a panel. Per-card front photos will still upload."}
                              </p>
                            )}
                            <p className="text-[11px] text-muted-foreground">
                              {withPhotos}/{totalCards} cards have front photos
                              — eBay accepts up to 12 pictures total.
                            </p>
                            {panelError && (
                              <p className="text-[11px] text-destructive">
                                {panelError}
                              </p>
                            )}

                            {/* Photo picker: reorder, include/exclude, gallery (TNK-28) */}
                            {photoCatalog.length > 0 && (
                              <div className="border-t border-border pt-2">
                                <PhotoOrderer
                                  items={photoCatalog}
                                  order={photoOrder}
                                  included={includedPhotos}
                                  onReorder={setPhotoOrder}
                                  onToggle={togglePhoto}
                                />
                              </div>
                            )}

                            {/* Watermark + logo overlays (TNK-25 phase 2b) */}
                            {panelImageRef && (
                              <div className="space-y-2 border-t border-border pt-2">
                                {watermarkAvailable ? (
                                  <div className="space-y-1">
                                    <label className="flex items-center gap-2 text-[11px] text-foreground">
                                      <input
                                        type="checkbox"
                                        checked={applyWatermark}
                                        onChange={(e) =>
                                          setApplyWatermark(e.target.checked)
                                        }
                                        className="h-3.5 w-3.5"
                                      />
                                      Apply watermark
                                      <span className="ml-auto tabular-nums text-muted-foreground">
                                        {Math.round(watermarkOpacity * 100)}%
                                      </span>
                                    </label>
                                    <input
                                      type="range"
                                      min={5}
                                      max={100}
                                      step={5}
                                      value={Math.round(watermarkOpacity * 100)}
                                      onChange={(e) =>
                                        setWatermarkOpacity(
                                          Number(e.target.value) / 100,
                                        )
                                      }
                                      disabled={!applyWatermark}
                                      className="w-full accent-primary disabled:opacity-40"
                                    />
                                  </div>
                                ) : (
                                  <p className="text-[11px] text-muted-foreground">
                                    No watermark uploaded —{" "}
                                    <Link
                                      to="/settings"
                                      className="font-medium text-primary hover:underline"
                                    >
                                      add one in Settings
                                    </Link>
                                    .
                                  </p>
                                )}

                                {logoAvailable ? (
                                  <div className="space-y-1">
                                    <label className="flex items-center gap-2 text-[11px] text-foreground">
                                      <input
                                        type="checkbox"
                                        checked={applyLogo}
                                        onChange={(e) =>
                                          setApplyLogo(e.target.checked)
                                        }
                                        className="h-3.5 w-3.5"
                                      />
                                      Apply logo (bottom-right corner)
                                      <span className="ml-auto tabular-nums text-muted-foreground">
                                        {Math.round(logoOpacity * 100)}%
                                      </span>
                                    </label>
                                    <input
                                      type="range"
                                      min={20}
                                      max={100}
                                      step={5}
                                      value={Math.round(logoOpacity * 100)}
                                      onChange={(e) =>
                                        setLogoOpacity(
                                          Number(e.target.value) / 100,
                                        )
                                      }
                                      disabled={!applyLogo}
                                      className="w-full accent-primary disabled:opacity-40"
                                    />
                                  </div>
                                ) : (
                                  <p className="text-[11px] text-muted-foreground">
                                    No logo uploaded —{" "}
                                    <Link
                                      to="/settings"
                                      className="font-medium text-primary hover:underline"
                                    >
                                      add one in Settings
                                    </Link>
                                    .
                                  </p>
                                )}

                                {compositeGenerating && (
                                  <p className="text-[11px] text-muted-foreground">
                                    Compositing overlays…
                                  </p>
                                )}
                                {compositeError && (
                                  <p className="text-[11px] text-destructive">
                                    {compositeError}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </>
              ))}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  if (panelImageRef) void deleteImage(panelImageRef);
                  if (compositeImageRef) void deleteImage(compositeImageRef);
                  setPanelImageRef(null);
                  setCompositeImageRef(null);
                  setPanelError(null);
                  setCompositeError(null);
                  setShowGoLiveForm(false);
                }}
                className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGoLive}
                disabled={ebayPushLoading}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-70"
              >
                {ebayPushLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Radio className="h-3.5 w-3.5" />
                )}
                {goLiveUploadProgress ??
                  (ebayPushLoading ? "Pushing to eBay…" : "Start Listing")}
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2 pt-2">
          {/* TNK-212 phase 2a — surface this bundle in the website storefront.
              A bundle's member cards are held ("In Package") from the moment
              it's MADE, so it's safe to list in any pre-sale state — deciding
              where to sell is the step right after making it. Pick/pack is
              post-sale fulfillment (before ship), so it isn't a prerequisite.
              Hidden once Sold/Shipped (the cards are gone). */}
          {!["Sold", "Shipped"].includes(product.status) && (
            <RequireRole min="manager">
              <button
                type="button"
                title={
                  product.storefrontListed
                    ? "Hide this bundle from the website store"
                    : "Show this bundle in the website store"
                }
                onClick={() =>
                  updateProduct({
                    ...product,
                    storefrontListed: !product.storefrontListed,
                  })
                }
                className={`flex w-full items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm font-semibold transition-colors ${
                  product.storefrontListed
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15"
                    : "border-border bg-card text-foreground hover:bg-muted"
                }`}
              >
                <Store className="h-4 w-4" />
                {product.storefrontListed
                  ? "Shown in website store"
                  : "Show in website store"}
              </button>
            </RequireRole>
          )}

          {/* TNK-212 phase 2a — once listed, set a custom store cover photo
              (else the tile uses a member card's photo). */}
          {product.storefrontListed &&
            !["Sold", "Shipped"].includes(product.status) && (
              <RequireRole min="manager">
                <BundleCoverControl
                  product={product}
                  updateProduct={updateProduct}
                />
              </RequireRole>
            )}

          {product.status === "Ready to List" &&
            !existingLiveListing &&
            !showGoLiveForm && (
              <RequireRole min="manager">
                <button
                  type="button"
                  onClick={() => {
                    void openGoLiveForm();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-red-500 px-4 py-3 text-sm font-semibold text-white hover:opacity-90"
                >
                  <Radio className="h-4 w-4" />
                  Go Live
                </button>
              </RequireRole>
            )}

          {/* Direct/website sale — skips Go-Live/eBay entirely and logs the
              sale here (venue = Website), flipping the product to Sold. */}
          {!["Sold", "Picked", "Packed", "Shipped"].includes(
            product.status,
          ) && (
            <RequireRole min="manager">
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    to: "/sales/create",
                    search: {
                      productId: id,
                      venue: "Website",
                      salePrice: String(product.targetSalePrice || ""),
                    },
                  })
                }
                className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted"
              >
                <DollarSign className="h-4 w-4" />
                Record direct sale
              </button>
            </RequireRole>
          )}

          {/* TNK-214 — picking is post-sale fulfillment: offer it once the
              product is Sold (or already Picked), not in pre-sale states. */}
          {["Sold", "Picked"].includes(product.status) && (
            <button
              type="button"
              onClick={() =>
                void navigate({
                  to: "/products/pick",
                  search: { productId: id },
                })
              }
              className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground hover:bg-card/80"
            >
              <span className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Pick Order
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          )}

          <PrimaryButton
            label={`Cycle Status → ${
              STATUS_CYCLE[
                (STATUS_CYCLE.indexOf(product.status) + 1) % STATUS_CYCLE.length
              ]
            }`}
            onClick={advanceStatus}
            variant="secondary"
          />
        </div>
      </div>
    </section>
  );
}

/**
 * TNK-212 phase 2a — custom store cover photo for a listed bundle. Uploads via
 * the shared image pipeline and stores the ref on `product.coverImageRef`; when
 * cleared, the store feed falls back to a member card's photo. Old refs are
 * best-effort deleted on replace/remove so we don't leak images.
 */
function BundleCoverControl({
  product,
  updateProduct,
}: {
  product: Product;
  updateProduct: (p: Product) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cover = product.coverImageRef
    ? imageUrl(product.coverImageRef, "thumb")
    : null;

  const onPick = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const ref = await uploadImage(file);
      const old = product.coverImageRef;
      updateProduct({ ...product, coverImageRef: ref });
      if (old) void deleteImage(old);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onRemove = (): void => {
    const old = product.coverImageRef;
    updateProduct({ ...product, coverImageRef: null });
    if (old) void deleteImage(old);
  };

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center gap-3">
        <div
          className="w-12 shrink-0 overflow-hidden rounded bg-muted"
          style={{ aspectRatio: "2.5 / 3.5" }}
        >
          {cover ? (
            <img src={cover} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImagePlus size={16} />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground">
            Store cover photo
          </p>
          <p className="text-[11px] text-muted-foreground">
            {product.coverImageRef
              ? "Custom photo"
              : "Using a member card's photo"}
          </p>
          {error && <p className="text-[11px] text-destructive">{error}</p>}
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="rounded-md border border-border px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
          >
            {busy ? "Uploading…" : product.coverImageRef ? "Replace" : "Upload"}
          </button>
          {product.coverImageRef && (
            <button
              type="button"
              onClick={onRemove}
              className="rounded-md px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void onPick(e.target.files?.[0])}
      />
    </div>
  );
}

function StatItem({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`text-lg font-bold ${color ?? "text-primary-foreground"}`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide opacity-75">
        {label}
      </div>
    </div>
  );
}

interface PackagedCardRowProps {
  card: Card;
  confirming: boolean;
  refreshing: boolean;
  noPriceFound: boolean;
  refreshError: boolean;
  historyExpanded: boolean;
  logPriceOpen: boolean;
  logPriceValue: string;
  logPriceSource: PriceSource;
  logPriceDate: string;
  logPriceError: string;
  onToggleHistory: () => void;
  onToggleLog: () => void;
  onLogValueChange: (v: string) => void;
  onLogSourceChange: (s: PriceSource) => void;
  onLogDateChange: (v: string) => void;
  onLogSave: () => void;
  onRefreshPrice: () => void;
  onRemove: () => void;
  onCancelRemove: () => void;
}

/** Small per-source icon for the sales-history list. Mirrors the inventory-detail
 *  helper of the same name so eBay / TCGplayer / Manual / Other rows are scannable. */
function PriceSourceIcon({ source }: { source: PriceSource }) {
  const cls = "h-3 w-3 shrink-0 text-muted-foreground";
  switch (source) {
    case "eBay Sold":
      return <ShoppingBag className={cls} aria-label="eBay sold" />;
    case "TCGplayer":
      return <TrendingUp className={cls} aria-label="TCGplayer" />;
    case "Manual":
      return <Pencil className={cls} aria-label="Manual entry" />;
    case "Other":
      return <Tag className={cls} aria-label="Other" />;
    default:
      return <Tag className={cls} />;
  }
}

function PackagedCardRow({
  card,
  confirming,
  refreshing,
  noPriceFound,
  refreshError,
  historyExpanded,
  logPriceOpen,
  logPriceValue,
  logPriceSource,
  logPriceDate,
  logPriceError,
  onToggleHistory,
  onToggleLog,
  onLogValueChange,
  onLogSourceChange,
  onLogDateChange,
  onLogSave,
  onRefreshPrice,
  onRemove,
  onCancelRemove,
}: PackagedCardRowProps) {
  const value = card.marketPrice || card.expectedSalePrice || 0;
  const hasHistory = !!card.priceHistory && card.priceHistory.length > 0;
  const pricedAtLabel = card.pricedAt
    ? `via ${card.priceSource ?? "?"} · ${new Date(
        card.pricedAt,
      ).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })}`
    : null;

  return (
    <li
      className={`rounded-md border bg-card p-3 ${
        confirming ? "border-destructive" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="truncate text-sm font-semibold text-foreground">
            {card.name}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {card.setName} · {card.rarity} · {card.condition}
          </div>
          {refreshError && (
            <div className="text-[11px] italic text-destructive">
              Could not fetch price — try again
            </div>
          )}
          {noPriceFound && !refreshError && (
            <div className="text-[11px] italic text-muted-foreground">
              No price found — set manually
            </div>
          )}
          {pricedAtLabel && !noPriceFound && !refreshError && (
            <div className="text-[11px] text-muted-foreground">
              {pricedAtLabel}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {hasHistory && (
              <button
                type="button"
                onClick={onToggleHistory}
                className="inline-flex items-center gap-1 text-[10px] text-secondary hover:underline"
              >
                {historyExpanded ? (
                  <ChevronUp className="h-2.5 w-2.5" />
                ) : (
                  <TrendingUp className="h-2.5 w-2.5" />
                )}
                {historyExpanded
                  ? "Hide history"
                  : `${card.priceHistory!.length} sale${card.priceHistory!.length === 1 ? "" : "s"} reported`}
              </button>
            )}
            <button
              type="button"
              onClick={onToggleLog}
              className="inline-flex items-center gap-1 text-[10px] text-secondary hover:underline"
            >
              {logPriceOpen ? (
                <X className="h-2.5 w-2.5" />
              ) : (
                <Plus className="h-2.5 w-2.5" />
              )}
              {logPriceOpen ? "Cancel" : "Log sale"}
            </button>
          </div>

          {historyExpanded && hasHistory && (
            <div className="mt-2 rounded-md border border-border p-2">
              {[...card.priceHistory!].reverse().map((entry, i) => (
                <div
                  key={`${entry.recordedAt}-${i}`}
                  className={`flex items-center justify-between gap-2 py-1 ${
                    i > 0 ? "border-t border-border" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <PriceSourceIcon source={entry.source} />
                    <div>
                      <div className="text-[11px] text-muted-foreground">
                        {new Date(entry.recordedAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {entry.source}
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    ${entry.price.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {logPriceOpen && (
            <div className="mt-2 rounded-md border border-border bg-muted/40 p-2">
              <div className="flex flex-wrap gap-1">
                {PRICE_SOURCES.map((src) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => onLogSourceChange(src)}
                    className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${
                      logPriceSource === src
                        ? "border-secondary bg-secondary text-secondary-foreground"
                        : "border-border bg-card text-foreground hover:bg-muted"
                    }`}
                  >
                    {src}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <div
                  className={`flex flex-1 items-center gap-1 rounded-md border bg-card px-2 ${
                    logPriceError ? "border-destructive" : "border-border"
                  }`}
                >
                  <span className="text-[11px] text-muted-foreground">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={logPriceValue}
                    onChange={(e) => onLogValueChange(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-transparent py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <div className="flex flex-[1.1] items-center gap-1 rounded-md border border-border bg-card px-2">
                  <Calendar className="h-2.5 w-2.5 text-muted-foreground" />
                  <input
                    type="text"
                    value={logPriceDate}
                    onChange={(e) => onLogDateChange(e.target.value)}
                    placeholder="Today"
                    className="w-full bg-transparent py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={onLogSave}
                  aria-label="Save price"
                  className="rounded-md bg-secondary p-1.5 text-primary hover:opacity-90"
                >
                  <Check className="h-3 w-3" />
                </button>
              </div>
              {logPriceError && (
                <div className="mt-1 text-[10px] text-destructive">
                  {logPriceError}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-foreground">
              ${value.toFixed(2)}
            </span>
            <button
              type="button"
              onClick={onRefreshPrice}
              disabled={refreshing}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-1 text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-60"
            >
              {refreshing ? (
                <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <RefreshCw className="h-2.5 w-2.5" />
              )}
              {refreshing ? "…" : "Refresh"}
            </button>
          </div>

          {confirming ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onCancelRemove}
                className="rounded-md bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="rounded-md bg-destructive px-2.5 py-1 text-[11px] font-semibold text-destructive-foreground hover:opacity-90"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove card from product"
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
